import React from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ArrowUpDown, Check, CheckCircle2, ChevronDown, Clock, Eye, XCircle } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../../utils/date';
import { FinishedGoodsProductionModal } from '../../../components/inventory/FinishedGoodsProductionModal';
import type {
  FinishedGoodsProductionListItem,
  FinishedGoodsProductionRequest,
} from '../../../services/inventory/inventoryMappers';
import type {
  FinishedGoodsProductionModalMode,
  FinishedGoodsProductionResult,
} from '../../../hooks/inventory-hooks/useFinishedGoodsProductionForm';
import {
  useFinishedGoodsProductions,
  type FgProductionSortKey,
  type FgProductionStatusFilter,
} from '../hooks/useFinishedGoodsProductions';
import { useWasteManagement } from '../hooks/useWasteManagement';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { onboardingService } from '../../../services/onboardingService';
import { WorkOrderConfirmModal } from './WorkOrderConfirmModal';
import { WasteManagementModal } from './WasteManagementModal';
import { WasteAdjustConfirmModal } from './WasteAdjustConfirmModal';

interface Props {
  enabled: boolean;
  productionOpen: boolean;
  productionMode: FinishedGoodsProductionModalMode;
  productionId?: string;
  onCloseProduction: () => void;
  onOpenProduction: (mode: FinishedGoodsProductionModalMode, id?: string) => void;
  roastTypeLabelMap: Record<string, string>;
  canWrite?: boolean;
}

const statusBadgeClass = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20';
    case 'draft':
      return 'bg-yellow-50 text-yellow-800 ring-1 ring-inset ring-yellow-600/20';
    case 'cancelled':
      return 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20';
    default:
      return 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-600/20';
  }
};

const statusDotClass = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-green-500';
    case 'draft':
      return 'bg-yellow-500';
    case 'cancelled':
      return 'bg-red-500';
    default:
      return 'bg-gray-400';
  }
};

const formatDatePart = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
};

const formatTimePart = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const SortableHeader: React.FC<{
  align?: 'left' | 'right';
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ align = 'left', active, onClick, children }) => (
  <th
    className={`group px-4 py-3 text-${align} text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100/70 transition-colors`}
    onClick={onClick}
  >
    <div className={`flex items-center gap-1.5 ${align === 'right' ? 'justify-end' : ''}`}>
      <span>{children}</span>
      {active ? (
        <ArrowUpDown className="h-3.5 w-3.5 text-primary-600" />
      ) : (
        <ArrowUpDown className="h-3.5 w-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </div>
  </th>
);

const StatusBadge: React.FC<{ status: string; productionId: string }> = ({ status, productionId }) => {
  const { t } = useLanguage();
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${statusBadgeClass(status)}`}
      data-test-id={`fg-production-status-${productionId}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${statusDotClass(status)}`} />
      {t(`workOrders.fgProductions.status.${status}`, undefined, status)}
    </span>
  );
};

const ProductionRow: React.FC<{
  production: FinishedGoodsProductionListItem;
  onComplete: (id: string) => void;
  onView: (id: string) => void;
  onCancel: (id: string) => void;
  canWrite?: boolean;
  /** True only for the throwaway draft the onboarding tour created. The tour's
   *  guide keys its "complete your draft" phase off this marker so it can never
   *  point at one of the company's real productions. */
  isOnboardingSample?: boolean;
}> = ({ production: p, onComplete, onView, onCancel, canWrite = true, isOnboardingSample = false }) => {
  const { t } = useLanguage();
  const createdTime = formatTimePart(p.created_at);
  return (
    <tr
      className="hover:bg-primary-50/30 transition-colors"
      data-onboarding-sample={isOnboardingSample ? 'true' : undefined}
    >
      <td
        className="px-4 py-3.5 whitespace-nowrap text-sm font-semibold text-gray-900 tabular-nums"
        data-test-id={`fg-production-code-${p.id}`}
      >
        {p.code || '—'}
      </td>
      <td className="px-4 py-3.5 text-sm text-gray-900 max-w-[260px]">
        <div className="flex flex-col leading-tight">
          <span className="font-medium truncate">{p.fg_name}</span>
          <span className="text-[11px] font-normal text-gray-400 uppercase tracking-wider mt-0.5">
            {p.fg_code || '-'}
          </span>
        </div>
      </td>
      <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-900 tabular-nums text-left">
        <span className="font-semibold">{p.qty_produced}</span>
      </td>
      <td className="px-4 py-3.5 whitespace-nowrap">
        <StatusBadge status={p.status} productionId={p.id} />
      </td>
      <td className="px-4 py-3.5 whitespace-nowrap">
        <div className="flex flex-col leading-tight">
          <span className="text-sm text-gray-900 tabular-nums">{formatDatePart(p.created_at)}</span>
          {createdTime ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 font-medium tabular-nums mt-1">
              <Clock className="h-3 w-3" />
              {createdTime}
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-4 py-3.5 whitespace-nowrap text-right">
        <div className="inline-flex items-center gap-2">
          {p.status === 'draft' && canWrite && (
            <button
              type="button"
              onClick={() => onComplete(p.id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 ring-1 ring-inset ring-primary-600/10 transition-colors"
              data-test-id={`fg-production-complete-${p.id}`}
            >
              <CheckCircle2 className="w-4 h-4" />
              {t('workOrders.fgProductions.actions.complete')}
            </button>
          )}
          <button
            type="button"
            onClick={() => onView(p.id)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 ring-1 ring-inset ring-primary-600/10 transition-colors"
            data-test-id={`fg-production-view-${p.id}`}
          >
            <Eye className="w-4 h-4" />
            {canWrite ? t('workOrders.fgProductions.actions.view') : t('common.view')}
          </button>
          {p.status === 'draft' && canWrite && (
            <button
              type="button"
              onClick={() => onCancel(p.id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-red-600 bg-red-50 rounded-md hover:bg-red-100 ring-1 ring-inset ring-red-600/10 transition-colors"
              data-test-id={`fg-production-cancel-${p.id}`}
            >
              <XCircle className="w-4 h-4" />
              {t('workOrders.fgProductions.actions.cancel')}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

export const FgProductionsPanel: React.FC<Props> = ({
  enabled,
  productionOpen,
  productionMode,
  productionId,
  onCloseProduction,
  onOpenProduction,
  roastTypeLabelMap,
  canWrite = true,
}) => {
  const { t } = useLanguage();
  const fg = useFinishedGoodsProductions({ enabled });
  const waste = useWasteManagement();
  const onboardingStep5 = useOnboardingStore(
    (s) => s.state?.status === 'in_progress' && s.state?.currentStep === 5,
  );
  // The draft the practice run created, recorded below on draft_created.
  const sampleProductionId = useOnboardingStore((s) => s.state?.sampleRefs?.fgProductionId);

  const handleProductionSuccess = async (result: FinishedGoodsProductionResult) => {
    if (result.kind === 'draft_created') {
      // Save closes the form (via onClose); the new draft now appears in the
      // panel. We intentionally do NOT auto-reopen the complete modal — during
      // onboarding the guide spotlights the panel's "Complete" button so the user
      // opens the completion step themselves, exactly like the normal flow.
      //
      // Record WHICH draft the practice run created. fg_productions has no
      // onboarding-sample column (the backend cleanup identifies practice
      // productions indirectly, by the sample roast they consume), so this id is
      // the only thing that tells the sample draft apart from the company's real
      // ones. Without it the guide's panel phase matched the first visible
      // `fg-production-complete-*` button and could point the user at completing
      // REAL production. Persisted server-side so it survives a mid-tour reload.
      if (onboardingStep5 && result.productionId) {
        const live = useOnboardingStore.getState().state;
        if (live && live.status === 'in_progress') {
          const sampleRefs = { ...(live.sampleRefs ?? {}), fgProductionId: result.productionId };
          // Store first: it is what marks the row this session, and the panel
          // refresh below must not be held up (or skipped on a throw) by the
          // round trip. The write only buys resume-after-reload.
          useOnboardingStore.getState().setState({ ...live, sampleRefs });
          void onboardingService
            .updateState({ ...live, sampleRefs })
            .catch((err) => console.error('Failed to record onboarding fg production ref:', err));
        }
      }
      await fg.refresh();
      return;
    }
    await fg.refresh();
    if (onboardingStep5) {
      // Practice run: skip the optional waste step (leftover roasted bulk is
      // cleaned up at tour end anyway) and signal the overlay to advance.
      window.dispatchEvent(
        new CustomEvent('qrema:finished-good-produced', {
          detail: { finishedGoodId: result.response.final_item_id },
        }),
      );
      return;
    }
    await waste.openWaste(result.request as FinishedGoodsProductionRequest);
  };

  const statusOptions: { value: FgProductionStatusFilter; label: string }[] = [
    { value: 'all', label: t('workOrders.fgProductions.status.all') },
    { value: 'draft', label: t('workOrders.fgProductions.status.draft') },
    { value: 'completed', label: t('workOrders.fgProductions.status.completed') },
    { value: 'cancelled', label: t('workOrders.fgProductions.status.cancelled') },
  ];
  const currentStatusLabel = statusOptions.find((o) => o.value === fg.statusFilter)?.label ?? statusOptions[0].label;

  const inputBaseClass =
    'block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2';

  return (
    <>
      <FinishedGoodsProductionModal
        isOpen={productionOpen}
        mode={productionMode}
        productionId={productionId}
        onClose={onCloseProduction}
        onSuccess={handleProductionSuccess}
      />
      <div className="bg-white p-4 rounded-lg shadow-sm" data-test-id="fg-productions-panel">
        <div className="mb-4">
          <div className="text-sm font-semibold text-gray-900">{t('workOrders.fgProductions.title')}</div>
          <div className="text-xs text-gray-500 mt-0.5">{t('workOrders.fgProductions.subtitle')}</div>
        </div>

        <div className="mb-4 flex flex-col md:flex-row flex-wrap gap-4">
          <div className="flex-1 min-w-0">
            <label htmlFor="fgSearchFilter" className="block text-sm font-medium text-gray-700">
              {t('workOrders.filters.search')}
            </label>
            <input
              id="fgSearchFilter"
              type="text"
              value={fg.searchFilter}
              onChange={(event) => fg.setSearchFilter(event.target.value)}
              placeholder={t('workOrders.fgProductions.searchPlaceholder')}
              className={`${inputBaseClass} mt-1`}
              data-test-id="fg-productions-search"
            />
          </div>

          <div className="w-full md:w-48">
            <label className="block text-sm font-medium text-gray-700">
              {t('workOrders.filters.status')}
            </label>
            <Listbox
              value={fg.statusFilter}
              onChange={(value: FgProductionStatusFilter) => fg.setStatusFilter(value)}
            >
              <div className="relative mt-1">
                <ListboxButton
                  data-test-id="fg-productions-status-filter"
                  className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm h-[38px]"
                >
                  <span className="block truncate">{currentStatusLabel}</span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <ListboxOptions className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {statusOptions.map((opt) => (
                    <ListboxOption
                      key={opt.value}
                      value={opt.value}
                      className={({ focus }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${focus ? 'bg-primary-100 text-primary-900' : 'text-gray-900'}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate capitalize ${selected ? 'font-medium' : 'font-normal'}`}>
                            {opt.label}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                              <Check className="h-4 w-4" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </div>
            </Listbox>
          </div>

          <div className="w-full md:w-44">
            <label htmlFor="fgCreatedFrom" className="block text-sm font-medium text-gray-700">
              {t('workOrders.fgProductions.filters.createdFrom', undefined, 'Created from')}
            </label>
            <input
              id="fgCreatedFrom"
              type="date"
              value={fg.createdFrom}
              onChange={(event) => fg.setCreatedFrom(event.target.value)}
              onClick={openNativeDatePickerSafely}
              onKeyDown={preventManualDateEntry}
              onPaste={preventManualDatePasteOrDrop}
              onDrop={preventManualDatePasteOrDrop}
              className={`${inputBaseClass} mt-1`}
              data-test-id="fg-productions-created-from"
            />
          </div>

          <div className="w-full md:w-44">
            <label htmlFor="fgCreatedTo" className="block text-sm font-medium text-gray-700">
              {t('workOrders.fgProductions.filters.createdTo', undefined, 'Created to')}
            </label>
            <input
              id="fgCreatedTo"
              type="date"
              value={fg.createdTo}
              onChange={(event) => fg.setCreatedTo(event.target.value)}
              onClick={openNativeDatePickerSafely}
              onKeyDown={preventManualDateEntry}
              onPaste={preventManualDatePasteOrDrop}
              onDrop={preventManualDatePasteOrDrop}
              className={`${inputBaseClass} mt-1`}
              data-test-id="fg-productions-created-to"
            />
          </div>
        </div>

        {fg.productionsError && (
          <div className="mb-3">
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-inset ring-red-600/20" data-test-id="fg-productions-error">
              {fg.productionsError}
            </div>
          </div>
        )}

        <div className="bg-white shadow-sm sm:rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/80 backdrop-blur-sm sticky top-0 z-10">
                <tr>
                  <SortableHeader active={fg.sortConfig.key === 'code'} onClick={() => fg.handleSort('code')}>
                    {t('workOrders.fgProductions.table.packingCode')}
                  </SortableHeader>
                  <SortableHeader active={fg.sortConfig.key === 'fg_name'} onClick={() => fg.handleSort('fg_name')}>
                    {t('workOrders.fgProductions.table.finishedGood')}
                  </SortableHeader>
                  <SortableHeader active={fg.sortConfig.key === 'qty_produced'} onClick={() => fg.handleSort('qty_produced')}>
                    {t('workOrders.fgProductions.table.qty')}
                  </SortableHeader>
                  <SortableHeader active={fg.sortConfig.key === 'status'} onClick={() => fg.handleSort('status')}>
                    {t('workOrders.fgProductions.table.status')}
                  </SortableHeader>
                  <SortableHeader active={fg.sortConfig.key === 'created_at'} onClick={() => fg.handleSort('created_at' as FgProductionSortKey)}>
                    {t('workOrders.fgProductions.table.created')}
                  </SortableHeader>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('workOrders.fgProductions.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {fg.productions.length === 0 && !fg.productionsLoading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center italic text-sm text-gray-500" data-test-id="fg-productions-empty">
                      {t('workOrders.fgProductions.empty')}
                    </td>
                  </tr>
                )}
                {fg.productions.map((p) => (
                  <ProductionRow
                    key={p.id}
                    production={p}
                    onComplete={(id) => onOpenProduction('complete_draft', id)}
                    onView={(id) => onOpenProduction('view', id)}
                    onCancel={(id) => fg.requestCancel(id)}
                    canWrite={canWrite}
                    isOnboardingSample={onboardingStep5 && p.id === sampleProductionId}
                  />
                ))}
                {fg.productionsLoading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500" data-test-id="fg-productions-loading">
                      {t('workOrders.fgProductions.loading')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <WasteManagementModal
        isOpen={waste.open}
        onClose={waste.closeWaste}
        rows={waste.rows}
        setRows={waste.setRows}
        error={waste.error}
        submitting={waste.submitting}
        editingKey={waste.editingKey}
        setEditingKey={waste.setEditingKey}
        onConfirm={waste.handleConfirm}
        roastTypeLabelMap={roastTypeLabelMap}
      />

      <WasteAdjustConfirmModal
        isOpen={waste.adjustConfirmOpen}
        onClose={waste.closeAdjustConfirm}
        onConfirm={waste.run}
        rows={waste.rows}
        submitting={waste.submitting}
        roastTypeLabelMap={roastTypeLabelMap}
      />

      <WorkOrderConfirmModal
        isOpen={fg.cancelConfirmOpen}
        onClose={fg.closeCancel}
        title={t('workOrders.fgProductions.cancelModal.title')}
        description={t('workOrders.fgProductions.cancelModal.description')}
        confirmLabel={t('workOrders.fgProductions.cancelModal.confirm')}
        confirmingLabel={t('workOrders.common.cancelling')}
        submitting={fg.cancelSubmitting}
        onConfirm={fg.submitCancel}
        testId="fg-cancel-confirm-modal"
        cancelTestId="fg-cancel-confirm-close"
        confirmTestId="fg-cancel-confirm-submit"
      />
    </>
  );
};
